import { usePublicLanguage } from './PublicLanguage';
import React, { useEffect, useState } from 'react';
import { LOGIN_URL, STUDY_URL } from './routes';
import { subscribeToPublicSession } from './publicSession';
import Footer from '../components/Footer/Footer';
import { Box, IconButton, Switch } from '@mui/material';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import MenuIcon from '@mui/icons-material/Menu';
import LanguageIcon from '@mui/icons-material/Language';
import { PUBLIC_LOCALES, supportedLocale } from './locales';

export interface PublicThemeProps {
  logo: string;
  dark?: boolean;
  onToggleTheme?: () => void;
}
export function PublicHeader({ logo, dark, onToggleTheme }: PublicThemeProps) {
  const { t, href: localHref, locale, changeLanguage, loading, error } = usePublicLanguage();
  const [session, setSession] = useState<'unknown' | 'signed-in' | 'signed-out'>('unknown');
  useEffect(() => subscribeToPublicSession(setSession), []);
  const accountLink =
    session === 'signed-in'
      ? (['Profile', '/study/#/profile'] as const)
      : session === 'signed-out'
        ? (['Sign in', LOGIN_URL] as const)
        : null;
  const closeOnEscape = (event: React.KeyboardEvent<HTMLElement>) => {
    const menu = event.currentTarget.closest('details');
    if (event.key === 'Escape' && menu) {
      menu.open = false;
      menu.querySelector('summary')?.focus();
    }
  };
  const links = [
    ['Study', STUDY_URL] as const,
    ['Explore', '/#explore'] as const,
    ['FAQ', '/faq/'] as const,
    ['Blog', '/blog/'] as const,
    ['Games', '/games/'] as const,
    ...(accountLink ? [accountLink] : []),
  ].map(([label, href]) => (
    <a
      key={t(label)}
      href={localHref(href)}
      onKeyDown={closeOnEscape}
      onClick={(event) => event.currentTarget.closest('details')?.removeAttribute('open')}
    >
      {t(label)}
    </a>
  ));
  return (
    <>
      <a className="home-skip" href="#main">
        {t('Skip to content')}
      </a>
      <header className="home-nav home-width">
        <a className="home-brand" href={localHref('/')} aria-label={t('Revelationary home')}>
          <img src={logo} alt="Revelationary" width="160" height="48" />
        </a>
        <div className="home-nav-controls">
          <label className="public-language-control">
            <LanguageIcon aria-hidden="true" fontSize="small" />
            <select
              aria-label={t('Website language')}
              value={locale}
              aria-busy={loading}
              onChange={(event) => {
                const next = supportedLocale(event.target.value);
                if (next) {
                  changeLanguage(next);
                }
              }}
            >
              {Object.entries(PUBLIC_LOCALES).map(([code, name]) => (
                <option key={code} value={code} lang={code}>
                  {name}
                </option>
              ))}
            </select>
          </label>
          {error && (
            <span role="alert" className="public-language-error">
              {t('Language could not be loaded. Please try again.')}
            </span>
          )}
          <nav className="home-desktop-nav" aria-label={t('Main navigation')}>
            {links}
          </nav>
          {onToggleTheme && (
            <>
              <Box
                sx={{ display: { xs: 'none', sm: 'flex' }, alignItems: 'center', color: '#a1a1a1' }}
              >
                <Brightness7Icon sx={{ fontSize: '1rem' }} />
                <Switch
                  size="small"
                  checked={!!dark}
                  onChange={onToggleTheme}
                  inputProps={{ 'aria-label': t('Toggle dark mode') }}
                  sx={{
                    mx: 0.5,
                    '& .MuiSwitch-switchBase.Mui-checked': { color: '#fff' },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                      backgroundColor: '#fff',
                    },
                  }}
                />
                <Brightness4Icon sx={{ fontSize: '1rem' }} />
              </Box>
              <IconButton
                onClick={onToggleTheme}
                aria-label={t(dark ? 'Use light theme' : 'Use dark theme')}
                sx={{ display: { xs: 'flex', sm: 'none' }, color: '#a1a1a1' }}
              >
                {dark ? <Brightness7Icon fontSize="small" /> : <Brightness4Icon fontSize="small" />}
              </IconButton>
            </>
          )}
          <details className="home-mobile-menu">
            <summary aria-label={t('Main menu')} onKeyDown={closeOnEscape}>
              <MenuIcon />
            </summary>
            <nav aria-label={t('Main navigation')}>{links}</nav>
          </details>
        </div>
      </header>
    </>
  );
}
export function PublicFooter() {
  return <Footer />;
}
export default function PublicLayout({
  logo,
  dark,
  onToggleTheme,
  children,
}: PublicThemeProps & { children: React.ReactNode }) {
  return (
    <div className="public-home" data-theme={dark ? 'dark' : 'light'}>
      <PublicHeader logo={logo} dark={dark} onToggleTheme={onToggleTheme} />
      <main id="main">{children}</main>
      <PublicFooter />
    </div>
  );
}
